///
/// EarthBrowser
///
/// Passkey debugging utilities
/// Copyright (c) 2025 Geodata Labs
///

import { isNativeApp } from "@/native/platform"

/**
 * Validate that the RPID matches the current origin
 * Returns diagnostic information if there's a mismatch
 */
export function validateRPID(rpId: string): { valid: boolean; message?: string; suggestion?: string } {
	if (typeof window === "undefined") {
		return { valid: true }
	}

	// Native apps: the passkey shim talks to the OS with the configured https
	// RP origin; the webview's own location (capacitor://app.earthbrowser.com
	// on iOS) is irrelevant to the ceremony
	if (isNativeApp()) {
		return { valid: true }
	}

	const hostname = window.location.hostname
	const protocol = window.location.protocol

	// Check if protocol is secure (https or localhost)
	if (protocol !== "https:" && hostname !== "localhost" && hostname !== "127.0.0.1") {
		return {
			valid: false,
			message: `WebAuthn requires HTTPS. Current protocol: ${protocol}`,
			suggestion: "Use HTTPS or access via localhost/127.0.0.1 for development",
		}
	}

	// Check if hostname matches or is a subdomain of the RPID
	// For production: rpId "earthbrowser.com" should work for "www.earthbrowser.com"
	// For development: rpId "localhost" should work for "localhost:3000"

	// Remove port from hostname
	const hostnameWithoutPort = hostname.split(":")[0]

	// Check direct match
	if (hostnameWithoutPort === rpId) {
		return { valid: true }
	}

	// Check if hostname is a subdomain of rpId (e.g., www.earthbrowser.com matches earthbrowser.com)
	if (hostnameWithoutPort.endsWith(`.${rpId}`)) {
		return { valid: true }
	}

	// Check common development scenarios
	if (
		(rpId === "localhost" && (hostnameWithoutPort === "127.0.0.1" || hostnameWithoutPort === "localhost")) ||
		(rpId === "127.0.0.1" && (hostnameWithoutPort === "127.0.0.1" || hostnameWithoutPort === "localhost"))
	) {
		return { valid: true }
	}

	// Invalid - provide diagnostic information
	return {
		valid: false,
		message: `RPID mismatch: Server returned "${rpId}" but you're accessing from "${hostnameWithoutPort}"`,
		suggestion:
			rpId === "localhost" && hostnameWithoutPort === "127.0.0.1"
				? 'Access the site via "localhost" instead of "127.0.0.1"'
				: rpId === "127.0.0.1" && hostnameWithoutPort === "localhost"
					? 'Access the site via "127.0.0.1" instead of "localhost"'
					: `Make sure you're accessing from the correct domain. Expected: ${rpId}, Got: ${hostnameWithoutPort}`,
	}
}

/**
 * Get diagnostic information about the current WebAuthn environment
 */
export function getWebAuthnDiagnostics() {
	if (typeof window === "undefined") {
		return null
	}

	return {
		origin: window.location.origin,
		hostname: window.location.hostname,
		protocol: window.location.protocol,
		port: window.location.port,
		isSecureContext: window.isSecureContext,
		hasPublicKeyCredential: typeof PublicKeyCredential !== "undefined",
	}
}
